import { Box, Button, Card, CardContent, CardHeader, Typography } from "@mui/material";

/**
 * Shown when we hold a valid session but can't reach the server to hydrate
 * it — a deploy restarting the API, a laptop waking before its wifi, a
 * dropped connection.
 *
 * The point is what this screen is NOT: the login page. Bouncing to /login
 * on an unreachable server is what made staff think the app "randomly logs
 * you out" — and, worse, it used to throw the tokens away on the way there,
 * turning a 20-second outage into a real re-login. The session is intact
 * behind this screen; the provider keeps retrying on its own and this
 * disappears by itself when the server answers. See docs/session-lifetime.md.
 */
export function SessionUnavailable({ onRetry }: { onRetry: () => void }): JSX.Element {
  return (
    <Box
      component="main"
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
      }}
    >
      <Card sx={{ maxWidth: "30rem", width: "100%" }}>
        <CardHeader title="Can't reach the XOP server" />
        <CardContent>
          <Typography sx={{ color: "text.secondary" }}>
            You're still signed in — we just can't load your session right now.
            This usually clears on its own within a minute (a brief network drop
            or a server update). We'll keep trying.
          </Typography>
          <Button variant="contained" onClick={onRetry} sx={{ mt: 3 }}>
            Try again now
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}
